import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Loader from "../dist/Loader";
import CustomButton from "./CustomButton";
import { showErrorToast } from "../dist/Toaster";
import XLSX from "xlsx-js-style";

const PobData = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const tableWrapperRef = useRef(null);

  const getMonthYearOptions = () => {
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];

    const options = [];
    const currentDate = new Date();
    const pastMonths = 10;
    const futureMonths = 2;
    const maxYearAllowed = currentDate.getFullYear() + 1;

    for (let i = futureMonths; i >= -pastMonths; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      const year = date.getFullYear();
      if (year > maxYearAllowed) continue;
      const monthIndex = date.getMonth();
      options.push({
        label: `${months[monthIndex]} ${year}`,
        value: `${String(monthIndex + 1).padStart(2, "0")}-${year}`,
      });
    }

    return options;
  };

  const monthYearOptions = getMonthYearOptions();
  const currentDate = new Date();
  const [pobMonthYear, setPobMonthYear] = useState(
    `${String(currentDate.getMonth() + 1).padStart(2, "0")}-${currentDate.getFullYear()}`,
  );

  const formatMonthYearForApi = (value) => {
    const [m, y] = value.split("-");
    return new Date(y, m - 1).toLocaleString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    try {
      let url = `${BASE_URL}/api/employees`;
      const params = [];
      if (stateId) params.push(`state_id=${stateId}`);
      if (divisionName && divisionName !== "All")
        params.push(`division=${encodeURIComponent(divisionName)}`);
      if (params.length) url += `?${params.join("&")}`;

      const res = await axios.get(url, { headers: getAuthHeaders() });
      setEmployees([{ id: "All", first_name: "All", last_name: "" }, ...res.data]);
      setSelectedEmployee("All");
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
      setError("Failed to load employees");
    }
  }, []);

  // Restore state from localStorage on component mount
  useEffect(() => {
    const savedState = localStorage.getItem("pobDataState");
    if (savedState) {
      try {
        const state = JSON.parse(savedState);

        setSelectedState(state.selectedState || "");
        setSelectedDivision(state.selectedDivision || "All");
        setSelectedEmployee(state.selectedEmployee || "All");
        setPobMonthYear(
          state.pobMonthYear || monthYearOptions[new Date().getMonth()].value,
        );
        setTableData(state.tableData || []);
        setShowContainers(Boolean(state.tableData && state.tableData.length > 0));
        setHasInitialLoad(Boolean(state.tableData && state.tableData.length > 0));

        const restoreStateId = state.selectedState || "";
        axios
          .get(
            `${BASE_URL}/api/divisions${restoreStateId ? `?state_id=${restoreStateId}` : ""}`,
            { headers: getAuthHeaders() },
          )
          .then((response) => {
            setDivisions([{ id: "All", name: "All" }, ...response.data]);
          })
          .catch((err) => {
            console.error("Error restoring divisions:", err);
            setDivisions([{ id: "All", name: "All" }]);
          });

        const divisionName =
          state.selectedDivision === "All"
            ? "All"
            : state.divisions?.find((d) => d.id === state.selectedDivision)?.name || "All";
        fetchEmployees(restoreStateId, divisionName);

        localStorage.removeItem("pobDataState");
      } catch (err) {
        console.error("Error restoring state:", err);
        localStorage.removeItem("pobDataState");
      }
    }
  }, [fetchEmployees, monthYearOptions]);

  // Fetch states on component mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        setStates(response.data);
      } catch (err) {
        console.error("Error fetching states:", err);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        const url = `${BASE_URL}/api/divisions${selectedState ? `?state_id=${selectedState}` : ""}`;
        const response = await axios.get(url, { headers: getAuthHeaders() });
        setDivisions([{ id: "All", name: "All" }, ...response.data]);
        setSelectedDivision("All");
      } catch (err) {
        console.error("Error fetching divisions:", err);
        setError("Failed to load divisions");
        setDivisions([{ id: "All", name: "All" }]);
      }
    };
    fetchDivisions();
  }, [selectedState]);

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");
    setError(null);

    try {
      const divisionRes = await axios.get(
        `${BASE_URL}/api/divisions${stateId ? `?state_id=${stateId}` : ""}`,
        { headers: getAuthHeaders() },
      );
      setDivisions([{ id: "All", name: "All" }, ...divisionRes.data]);
    } catch (err) {
      console.error("Error loading divisions", err);
      setError("Failed to load divisions");
      setDivisions([{ id: "All", name: "All" }]);
    }

    fetchEmployees(stateId, "All");
  };

  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("All");
    setError(null);
    const division = divisions.find((d) => String(d.id) === divisionId);
    const divisionName = division?.name || "All";
    fetchEmployees(selectedState, divisionName);
  };

  const handleGoClick = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [selectedMonth, selectedYear] = pobMonthYear.split("-");
      const monthYear = formatMonthYearForApi(pobMonthYear);
      let url = `${BASE_URL}/api/sob-targets/employee-stockist-monthly-summary?month_year=${encodeURIComponent(monthYear)}`;

      if (selectedEmployee && selectedEmployee !== "All") {
        url += `&employee_id=${selectedEmployee}`;
      }
      if (selectedState) {
        url += `&state_id=${selectedState}`;
      }
      if (selectedDivision && selectedDivision !== "All") {
        const div = divisions.find((d) => String(d.id) === String(selectedDivision));
        if (div?.name) {
          url += `&division=${encodeURIComponent(div.name)}`;
        }
      }

      const res = await axios.get(url, { headers: getAuthHeaders() });
      const list = Array.isArray(res.data) ? res.data : [];
      const mapped = list.map((item, index) => ({
        id: `${item.employee_id || item.emp_id}-${item.stockist_id}-${selectedMonth}-${selectedYear}`,
        sob_id: item.sob_id,
        employee_id: item.emp_id || null,
        stockist_id: item.stockist_id,
        month: Number(selectedMonth),
        year: Number(selectedYear),
        sno: index + 1,
        state: item.state || "-",
        empId: item.user_id || "-",
        employee: item.employee || "-",
        designation: item.designation || "-",
        division: item.division || "-",
        manager: item.manager || "-",
        stockistCode: item.stockist_code || "-",
        stockist: item.stockist_name || "-",
        hq: item.hq || "-",
        city: item.city || "-",
        phone: item.phone || "-",
        target: Number(item.stk_sob_target || 0),
        pob: Number(item.pob || 0),
        sob: Number(item.sob || 0),
        isEditing: false,
        tempTarget: String(item.stk_sob_target || 0),
        tempSob: String(item.sob || 0),
      }));

      setTableData(mapped);
      setShowContainers(true);
      setHasInitialLoad(true);
    } catch (err) {
      console.error("POB API Error:", err);
      setError(err.response?.data?.message || "Failed to load data");
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  }, [pobMonthYear, selectedEmployee, selectedState, selectedDivision, divisions]);

  useEffect(() => {
    if (showContainers && hasInitialLoad) {
      const timeoutId = setTimeout(() => {
        handleGoClick();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [showContainers, hasInitialLoad, handleGoClick]);

  // PRINT / EXPORT helpers
  const printableStyles = `
    <style>
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial; font-size:12px; color:#111; }
      table { border-collapse: collapse; width: 100%; table-layout: fixed; word-break: break-word; }
      th, td { border: 1px solid #999; padding: 6px 8px; vertical-align: middle; }
      thead th { background: #2b6cb0; color: #fff; font-weight: 600; text-align: center; }
      .text-center { text-align: center; }
      .text-left { text-align: left; }
      .nowrap { white-space: nowrap; }
      .small { font-size: 11px; }
      a { color: #1d4ed8; text-decoration: underline; }
    </style>
  `;

  const getPrintableTableHtml = () => {
    if (!tableWrapperRef.current) return "<div>No table found</div>";
    const wrapper = tableWrapperRef.current.cloneNode(true);

    wrapper.querySelectorAll("button").forEach((btn) => {
      const span = document.createElement("span");
      span.innerHTML = btn.innerHTML;
      btn.parentNode.replaceChild(span, btn);
    });

    wrapper.querySelectorAll("a").forEach((a) => {
      const href = a.getAttribute("href");
      if (href) a.insertAdjacentText("afterend", ` (${href})`);
    });

    return wrapper.innerHTML;
  };

  const handleEditStart = (id) => {
    setTableData((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, isEditing: true, tempTarget: String(r.target), tempSob: String(r.sob) }
          : r,
      ),
    );
  };

  const handleTargetChange = (id, v) => {
    setTableData((prev) =>
      prev.map((r) => (r.id === id ? { ...r, tempTarget: v } : r)),
    );
  };

  const handleSobChange = (id, v) => {
    setTableData((prev) =>
      prev.map((r) => (r.id === id ? { ...r, tempSob: v } : r)),
    );
  };

  const handleCancel = (id) => {
    setTableData((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, isEditing: false, tempTarget: String(r.target), tempSob: String(r.sob) }
          : r,
      ),
    );
  };

  const handleUpdate = async (id) => {
    const row = tableData.find((r) => r.id === id);
    if (!row) return;

    if (row.month === null || row.year === null) {
      showErrorToast("Invalid month/year. Please reload.");
      return;
    }

    const payload = {
      sob_id: row.sob_id,
      employee_id: Number(row.employee_id),
      stockist_id: row.stockist_id,
      month: row.month,
      year: row.year,
      stk_sob_target: parseFloat(row.tempTarget) || 0,
      sob: parseFloat(row.tempSob) || 0,
    };

    try {
      const res = await axios.put(
        `${BASE_URL}/api/sob-targets/update-sob-target`,
        payload,
        { headers: getAuthHeaders() },
      );

      const newSobId = res.data?.sob_id;
      setTableData((prev) =>
        prev.map((r) =>
          r.id === id
            ? {
                ...r,
                sob_id: newSobId ?? r.sob_id,
                target: parseFloat(r.tempTarget) || 0,
                sob: parseFloat(r.tempSob) || 0,
                isEditing: false,
              }
            : r,
        ),
      );
    } catch (err) {
      console.error("Update failed", err);
      showErrorToast("Update failed");
    }
  };

  const handlePrint = () => {
    try {
      const tableHtml = getPrintableTableHtml();
      const w = window.open(
        "",
        "_blank",
        "width=1200,height=900,menubar=no,toolbar=no,status=no,scrollbars=yes",
      );
      if (!w) {
        showErrorToast("Please allow popups to enable printing.");
        return;
      }

      w.document.open();
      w.document.write(
        `<html><head><title>POB Report - Print</title>${printableStyles}</head><body>`,
      );
      w.document.write(
        `<div style="margin:12px 8px;"><h2 style="margin-bottom:8px;text-align:center">POB Report</h2>`,
      );
      w.document.write(tableHtml);
      w.document.write(
        `<div style="text-align:center;margin-top:12px;font-size:12px">Generated on: ${new Date().toLocaleString()}</div>`,
      );
      w.document.write("</div></body></html>");
      w.document.close();

      w.focus();
      setTimeout(() => {
        try {
          w.print();
        } catch (e) {
          console.error("Print failed", e);
        }
      }, 300);
    } catch (err) {
      console.error("Print error", err);
      showErrorToast("Unable to print. Please try again.");
    }
  };

const handleExport = () => {
  if (tableData.length === 0) {
    showErrorToast("No data to export");
    return;
  }

  const headers = [
    "S.No", "State", "Emp ID", "Employee", "Designation", "Division",
    "Manager", "Stockist Code", "Stockist", "HQ", "City", "Phone",
    "Target", "POB", "SOB",
  ];

  const fixValue = (val) => {
    const raw = String(val ?? "").trim();
    return /^\d+$/.test(raw) ? Number(raw) : raw;
  };

  const totalTarget = tableData.reduce((sum, r) => sum + Number(r.target || 0), 0);
  const totalPob    = tableData.reduce((sum, r) => sum + Number(r.pob    || 0), 0);
  const totalSob    = tableData.reduce((sum, r) => sum + Number(r.sob    || 0), 0);

  const wsData = [
    headers,
    ...tableData.map((row, idx) => [
      idx + 1,
      row.state,
      fixValue(row.empId),
      row.employee,
      row.designation,
      row.division,
      row.manager,
      fixValue(row.stockistCode),
      row.stockist,
      row.hq,
      row.city,
      row.phone,
      Number(row.target || 0),
      Number(row.pob    || 0),
      Number(row.sob    || 0),
    ]),
    ["", "", "", "", "", "", "", "", "", "", "", "TOTAL", totalTarget, totalPob, totalSob],
  ];

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // =====================================
  // FORCE TYPES (Emp ID + Stockist Code)
  // =====================================

  const range = XLSX.utils.decode_range(ws["!ref"]);

  for (let R = 1; R <= range.e.r; ++R) {
    let cell = ws[XLSX.utils.encode_cell({ r: R, c: 2 })]; // Emp ID
    if (cell) {
      const raw = String(cell.v ?? "").trim();
      cell.t = /^\d+$/.test(raw) ? "n" : "s";
    }

    cell = ws[XLSX.utils.encode_cell({ r: R, c: 7 })]; // Stockist Code
    if (cell) {
      const raw = String(cell.v ?? "").trim();
      cell.t = /^\d+$/.test(raw) ? "n" : "s";
    }
  }

  // =====================================
  // STYLES
  // =====================================

  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
    fill: { fgColor: { rgb: "2563EB" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: {
      top:    { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left:   { style: "thin", color: { rgb: "000000" } },
      right:  { style: "thin", color: { rgb: "000000" } },
    },
  };

  const rowStyle = {
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top:    { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left:   { style: "thin", color: { rgb: "000000" } },
      right:  { style: "thin", color: { rgb: "000000" } },
    },
  };

  const totalRowStyle = {
    font: { bold: true },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top:    { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left:   { style: "thin", color: { rgb: "000000" } },
      right:  { style: "thin", color: { rgb: "000000" } },
    },
  };

  // =====================================
  // APPLY STYLES TO ALL CELLS
  // =====================================

  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });

      if (!ws[cellAddress]) {
        ws[cellAddress] = { t: "z", v: "" };
      }

      if (R === 0) {
        ws[cellAddress].s = headerStyle;                      // Header row
      } else if (R === range.e.r) {
        ws[cellAddress].s = totalRowStyle;                    // Total row
      } else {
        ws[cellAddress].s = rowStyle;                         // Data rows
      }
    }
  }

  // =====================================
  // COLUMN WIDTHS
  // =====================================

  ws["!cols"] = [
    { wch: 8  },  // S.No
    { wch: 15 },  // State
    { wch: 14 },  // Emp ID
    { wch: 28 },  // Employee
    { wch: 18 },  // Designation
    { wch: 18 },  // Division
    { wch: 24 },  // Manager
    { wch: 18 },  // Stockist Code
    { wch: 28 },  // Stockist
    { wch: 15 },  // HQ
    { wch: 15 },  // City
    { wch: 16 },  // Phone
    { wch: 14 },  // Target
    { wch: 14 },  // POB
    { wch: 14 },  // SOB
  ];

  // =====================================
  // FREEZE HEADER
  // =====================================

  ws["!freeze"] = { xSplit: 0, ySplit: 1 };

  // =====================================
  // FILTERS
  // =====================================

  ws["!autofilter"] = { ref: "A1:O1" };

  // =====================================
  // EXPORT
  // =====================================

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "POBReport");

  const fileName = `POB_Report_${selectedState || "All"}_${pobMonthYear}_${new Date()
    .toISOString()
    .slice(0, 10)}.xlsx`;

  XLSX.writeFile(wb, fileName);
};

  const handleGoButtonClick = () => {
    setShowContainers(true);
    handleGoClick();
  };

  const handleRetry = () => {
    setError(null);
    handleGoClick();
  };

  return (
    <div className="p-4 border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}
      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">POB</span>{" "}
          <span className="text-blue-500">Report</span>
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
          <label className="text-right text-xs">State</label>
          <select
            className="w-full mt-1 border rounded px-2 py-1 text-xs"
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            disabled={isLoading}
          >
            <option value="">All</option>
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="text-right text-xs">Division</label>
          <select
            className="w-full mt-1 border rounded px-2 py-1 text-xs"
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
            disabled={isLoading}
          >
            {divisions.map((division) => (
              <option key={division.id} value={division.id}>
                {division.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center mt-3">
          <label className="text-right text-xs">Employee</label>
          <select
            className="w-full mt-1 border rounded px-2 py-1 text-xs"
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            disabled={isLoading}
          >
            <option key="All" value="All">All</option>
            {employees
              .filter((emp) => String(emp.id) !== "All")
              .map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {`${emp.first_name} ${emp.last_name} | ${emp.employee} | ${emp.designation || ""} | ${emp.headquarter_name || ""} | ${emp.division || ""} | ${emp.state_name || ""}`}
                </option>
              ))}
          </select>

          <label className="text-right text-xs">Month / Year</label>
          <select
            className="w-full mt-1 border rounded px-2 py-1 text-xs"
            value={pobMonthYear}
            onChange={(e) => setPobMonthYear(e.target.value)}
            disabled={isLoading}
          >
            {monthYearOptions.map((option, idx) => (
              <option key={idx} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-center mt-4">
          <CustomButton
            variant="go"
            onClick={handleGoButtonClick}
            disabled={isLoading}
            className="break-normal"
          >
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>

        {error && (
          <div className="mt-3 text-center text-red-600">
            {error}
            <button
              onClick={handleRetry}
              className="ml-2 text-blue-600 hover:text-blue-800"
            >
              Retry
            </button>
          </div>
        )}
      </div>

      {showContainers && (
        <div ref={tableWrapperRef} className="mt-4">
          <table className="unlock-dcr-leave-table text-sm">
            <thead className="bg-blue-500 text-white">
              <tr>
                {[
                  "S.No", "State", "Emp ID", "Employee", "Desig Nation",
                  "Division", "Manager", "Stockist Code", "Stockist", "HQ",
                  "City", "Phone", "Target", "POB", "SOB", "",
                ].map((h) => (
                  <th
                    key={h}
                    className="border border-gray-500 px-2 py-2 text-sm font-semibold text-center"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {tableData.map((row) => (
                <tr
                  key={row.id}
                  className={`border ${
                    row.isEditing ? "bg-green-200" : "bg-white hover:bg-gray-50"
                  }`}
                >
                  <td className="border px-2 text-center">{row.sno}</td>
                  <td className="border px-2">{row.state}</td>
                  <td className="border px-2">{row.empId}</td>
                  <td className="border px-2">{row.employee}</td>
                  <td className="border px-2">{row.designation}</td>
                  <td className="border px-2">{row.division}</td>
                  <td className="border px-2">{row.manager}</td>
                  <td className="border px-2">{row.stockistCode}</td>
                  <td className="border px-2">{row.stockist}</td>
                  <td className="border px-2">{row.hq}</td>
                  <td className="border px-2">{row.city}</td>
                  <td className="border px-2">{row.phone}</td>

                  {/* TARGET */}
                  <td className="border px-2 text-right">
                    {row.isEditing ? (
                      <input
                        type="number"
                        value={row.tempTarget}
                        onChange={(e) => handleTargetChange(row.id, e.target.value)}
                        className="w-full border px-1 py-1 bg-white text-right"
                      />
                    ) : (
                      row.target.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    )}
                  </td>

                  {/* POB */}
                  <td className="border px-2 text-right">
                    {row.pob.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>

                  {/* SOB */}
                  <td className="border px-2 text-right">
                    {row.isEditing ? (
                      <input
                        type="number"
                        value={row.tempSob}
                        onChange={(e) => handleSobChange(row.id, e.target.value)}
                        className="w-full border px-1 py-1 bg-white text-right"
                      />
                    ) : (
                      row.sob.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    )}
                  </td>

                  {/* ACTION */}
                  <td className="border px-2 py-1 text-center">
                    {row.isEditing ? (
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => handleUpdate(row.id)}
                          className="px-2 py-1 bg-gray-200 border text-xs font-semibold"
                        >
                          UPDATE
                        </button>
                        <button
                          onClick={() => handleCancel(row.id)}
                          className="px-2 py-1 bg-gray-200 border text-xs font-semibold"
                        >
                          CANCEL
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEditStart(row.id)}
                        className="px-3 py-1 bg-gray-200 border text-xs font-semibold"
                      >
                        EDIT
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr className="bg-blue-700 text-white font-semibold">
                <td colSpan="12" className="border border-gray-500 px-2 py-2 text-right">
                  TOTAL
                </td>
                <td className="border border-gray-500 px-2 py-2 text-right">
                  {tableData
                    .reduce((s, r) => s + Number(r.target || 0), 0)
                    .toLocaleString()}
                </td>
                <td className="border border-gray-500 px-2 py-2 text-right">
                  {tableData
                    .reduce((s, r) => s + Number(r.pob || 0), 0)
                    .toLocaleString()}
                </td>
                <td className="border border-gray-500 px-2 py-2 text-right">
                  {tableData
                    .reduce((s, r) => s + Number(r.sob || 0), 0)
                    .toLocaleString()}
                </td>
                <td className="border border-gray-500"></td>
              </tr>
            </tfoot>
          </table>

          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} label="Print" variant="print">
              🖨
            </CustomButton>
            <CustomButton onClick={handleExport} label="Export" variant="export">
              📥
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default PobData;